import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertCircle, Filter, User, Calendar, Home, FileText, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const PBDashboard = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, complete: 0, incomplete: 0, no_type_selected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [search, setSearch] = useState('');
  const [sendingWeekly, setSendingWeekly] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/compliance/overview?status_filter=${statusFilter}&only_incomplete=${onlyIncomplete}`);
      setRows(res.data.rows || []);
      setSummary(res.data.summary || {});
    } catch (e) {
      toast.error('Failed to load compliance overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, onlyIncomplete]);

  const sendWeeklyNow = async () => {
    if (!window.confirm('Send the weekly compliance summary email to all agents and brokers right now?')) return;
    setSendingWeekly(true);
    try {
      const res = await api.post('/compliance/weekly-summary/send-now');
      toast.success(`Sent to ${res.data.agent_emails_sent} agents`);
    } catch (e) {
      toast.error('Failed to send weekly summary');
    } finally {
      setSendingWeekly(false);
    }
  };

  const filteredRows = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.agent_name || '').toLowerCase().includes(q)
      || (r.property_address || '').toLowerCase().includes(q)
      || (r.transaction_type || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="pb-dashboard">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Principal Broker · Compliance Oversight</h2>
          <p className="text-sm text-gray-600 mt-1">Every transaction across all agents. Required forms tracked per transaction type.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="pb-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button onClick={sendWeeklyNow} disabled={sendingWeekly} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="pb-send-weekly">
            <Mail className="w-4 h-4 mr-2" />{sendingWeekly ? 'Sending…' : 'Send Weekly Summary Now'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-[#BCA480]" data-testid="pb-total">{summary.total}</div><div className="text-xs text-gray-500">Total transactions</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-emerald-600" data-testid="pb-complete">{summary.complete}</div><div className="text-xs text-gray-500">Fully compliant</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-red-600" data-testid="pb-incomplete">{summary.incomplete}</div><div className="text-xs text-gray-500">Missing forms</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold text-amber-600" data-testid="pb-no-type">{summary.no_type_selected}</div><div className="text-xs text-gray-500">No type selected</div></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1 bg-gray-100 rounded p-1">
          {[['active','Active'],['closed','Closed'],['all','All']].map(([v,l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} className={`px-3 py-1 text-sm rounded ${statusFilter === v ? 'bg-white shadow font-semibold' : 'text-gray-600'}`} data-testid={`pb-filter-${v}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyIncomplete} onChange={(e) => setOnlyIncomplete(e.target.checked)} data-testid="pb-only-incomplete" />
          <span>Only incomplete</span>
        </label>
        <Input
          placeholder="Search agent, property, type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="pb-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-gray-500">No transactions match the current filters.</CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="pb-table">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="text-left px-3 py-2 border-b">Property</th>
                <th className="text-left px-3 py-2 border-b">Agent</th>
                <th className="text-left px-3 py-2 border-b">Type</th>
                <th className="text-left px-3 py-2 border-b">Side</th>
                <th className="text-left px-3 py-2 border-b">Close</th>
                <th className="text-left px-3 py-2 border-b">Compliance</th>
                <th className="text-left px-3 py-2 border-b">Missing forms</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => {
                const c = r.compliance;
                return (
                  <tr key={r.transaction_id} className="border-b hover:bg-gray-50" data-testid={`pb-row-${r.transaction_id}`}>
                    <td className="px-3 py-2 font-medium">{r.property_address}</td>
                    <td className="px-3 py-2">{r.agent_name}<br /><span className="text-[11px] text-gray-500">{r.agent_email}</span></td>
                    <td className="px-3 py-2 text-xs">{r.transaction_type || <em className="text-amber-600">not set</em>}</td>
                    <td className="px-3 py-2 capitalize text-xs">{r.side}</td>
                    <td className="px-3 py-2 text-xs">{r.closing_date || '—'}</td>
                    <td className="px-3 py-2">
                      {c.complete
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border">✅ 100%</Badge>
                        : <Badge className="bg-red-100 text-red-800 border-red-300 border">⚠️ {c.percent_complete}% ({c.required_uploaded}/{c.required_total})</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs text-red-700 max-w-xs">
                      {c.required_missing?.length ? c.required_missing.join(', ') : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PBDashboard;
